import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Grid, TextField, ButtonGroup, FormControl, MenuItem, Select } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import useInput from 'lib/hooks/useInput';
import { useDidUpdateEffect } from 'lib/utils';
import MuiWrapper from 'components/common/input/MuiWrapper';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import { icon_user_circle, icon_trash } from 'components/base/images';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import ImgCrop from 'components/common/images/ImgCrop';
import CustomSpan from 'components/common/text/CustomSpan';
import IconButton from '@material-ui/core/IconButton';
import Color from 'color';
import { getStringByte, regPhoneNumber, validInputString } from 'lib/library';
import { AppActions } from 'store/actionCreators';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { useTranslation } from 'react-i18next';
import { IPartnerProfileImage, IPartnerData, IAccountTypeList } from 'lib/types/partnerTypes';
import { partnerTypeList } from 'lib/mapper';

interface PartnerModalInputProps {
  minorData: {
    value: string | null;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  majorData: {
    value: string | null;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };

  currencyList?: any | null;
  countryList?: any | null;
  // regionList?: any | null;
  partnerData: IPartnerData;
  profileImageData: IPartnerProfileImage;
  accountTypeList: IAccountTypeList[];
  onSubmitPartnerData: (data: PartnerInformationFormInput) => void;
  onDeletePartner: () => void;
}

interface PartnerInformationFormInput {
  nickName: string;
  type: number;
  phone: string;
  phoneCode: number;
  email: string;
  fax: string;
  tel: string;
  address: string;
  currency: string;
  country: number;
}

/**
 * @param {PartnerModalInputProps} { selectedPartner }
 * @return {*}
 */
function PartnerModalInput({
  majorData,
  minorData,

  partnerData,
  profileImageData,
  accountTypeList,
  currencyList,
  countryList,
  onSubmitPartnerData,
  onDeletePartner,
}: PartnerModalInputProps) {
  const {
    control: partnerInfoControl,
    handleSubmit: handleSubmitInformation,
    errors: partnerInfoErros,
    setValue: setPartnerInfo,
    watch: partnerInfoWatch,
    getValues: getPartnerInfo,
    reset,
    formState: { isValid, errors, isDirty },
    clearErrors,
  } = useForm<PartnerInformationFormInput>({
    mode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: false,
  });

  const {
    userGroupIdx,
    nickName,
    type,
    phone,
    phoneCode,
    email,
    fax,
    tel,
    address,
    currency,
    accountType,
    country,
  } = partnerData;

  const { t } = useTranslation();
  const { profileImage, profileIdx, deleteProfileImageIdx } = profileImageData;

  const isModify = useInput(false);

  const [isMenualUser, setIsMenualUser] = useState(false);
  const profileFileRef = useRef<null | any>(null);
  const [profileFilePreview, setProfileFilePreview] = useState<object | any | null>(null);

  const deleteProfileImg = useInput('');

  const [isImageChange, setIsImageChange] = useState<boolean>(false);

  const profileNotification = {
    title: t('GLOBAL_ALERT'),
    mainText: t('NOT_USE_PRORILE_IMAGE'),
    subText: t('NOT_USE_PRORILE_IMAGE_DETAIL'),
  };

  const alertNotificationPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={profileNotification.title}
          mainText={profileNotification.mainText}
          subText={profileNotification.subText}
        />
      ),
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  useEffect(() => {
    // accountType 판별
    if (!!accountType.value) {
      setIsMenualUser(
        accountTypeList.find((i: any) => i.id === accountType.value)?.type === 'manual',
      );
    } else {
      //accountType.value가 없는 경우, new로 간주
      setIsMenualUser(true);
    }
  }, [accountType.value]);

  useEffect(() => {
    if (minorData.value === 'new') {
      isModify.setValue(true);
    }
    if (minorData.value === 'detail') {
      isModify.setValue(false);
    }
  }, [minorData.value]);

  useDidUpdateEffect(() => {
    if (minorData.value === 'detail') {
      setPartnerInfo('nickName', nickName.value);
      setPartnerInfo('type', type.value);
      setPartnerInfo('phone', phone.value);
      setPartnerInfo('phoneCode', phoneCode.value);
      setPartnerInfo('email', email.value);
      setPartnerInfo('fax', fax.value);
      setPartnerInfo('tel', tel.value);
      setPartnerInfo('address', address.value);
      setPartnerInfo('country', country.value);
      setPartnerInfo('currency', currency.value);

      deleteProfileImg.setValue('');
      setProfileFilePreview(!!profileImage.value ? profileImage.value : null);
    }
  }, [partnerData, profileImageData]);

  useDidUpdateEffect(() => {
    if (!!isModify.value) {
      setPartnerInfo('nickName', nickName.value);
      setPartnerInfo('type', type.value);
      setPartnerInfo('phone', phone.value);
      setPartnerInfo('phoneCode', phoneCode.value);
      setPartnerInfo('email', email.value);
      setPartnerInfo('fax', fax.value);
      setPartnerInfo('tel', tel.value);
      setPartnerInfo('address', address.value);
      setPartnerInfo('country', country.value);
      setPartnerInfo('currency', currency.value);

      deleteProfileImg.setValue('');
      setProfileFilePreview(!!profileImage.value ? profileImage.value : null);
    }
  }, [isModify.value]);

  useEffect(() => {
    //init
    reset({
      nickName: nickName.value,
      type: type.value,
      phone: phone.value,
      phoneCode: phoneCode.value,
      email: email.value,
      fax: fax.value,
      tel: tel.value,
      address: address.value,
      country: country.value,
      currency: currency.value,
    });

    deleteProfileImg.setValue('');
    setProfileFilePreview(null);
    setIsImageChange(false);
    clearErrors([
      'nickName',
      'type',
      'phone',
      'phoneCode',
      'email',
      'fax',
      'tel',
      'address',
      'country',
      'currency',
    ]);
  }, []);

  const handleChangeProfileImage = (event: any) => {
    let reader = new FileReader();
    let file = event.target.files[0];

    if (!!file) {
      if (
        file.size > 10485760 ||
        file.type.split('/')[0] !== 'image' ||
        file.name.split('.')[0].length > 124
      ) {
        alertNotificationPopup();
      } else {
        reader.onloadend = () => {
          setProfileFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
        profileImage.onChange(event);
        setIsImageChange(true);
      }
    } else {
      setIsImageChange(true);
      return;
    }
  };

  const onError = () => {};

  const handleDeleteProfileImage = () => {
    setProfileFilePreview(null);
    profileFileRef.current.value = null;
    setIsImageChange(true);
    profileImage.setValue({});
    if (!!profileIdx.value) {
      deleteProfileImageIdx.setValue(profileIdx.value);
    }
  };

  const checkKeyDown = (e: any) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  return (
    <StyledPartnerModalInput data-component-name="PartnerModalInput">
      <div className="partnerModalInput__container">
        <div className="partnerModalInput__wrapper">
          <form
            onSubmit={handleSubmitInformation(onSubmitPartnerData, onError)}
            onKeyDown={e => checkKeyDown(e)}
          >
            <Grid container className="partnerModalInput__form_container">
              <Grid
                container
                item
                xs={12}
                className={cx('partnerModalInput__form_wrapper top', {
                  modify: !!isModify.value,
                })}
              >
                <Grid
                  item
                  className={cx('partnerModalInput__form_box profileImg', {
                    modify: !!isModify.value,
                  })}
                >
                  <label
                    htmlFor="partnerImage"
                    className={cx('profileImg__box', {
                      cursorPointer: minorData.value !== 'detail' && !!isMenualUser ? true : false,
                    })}
                  >
                    {!!profileFilePreview ? (
                      <ImgCrop width={80} isCircle src={profileFilePreview} />
                    ) : (
                      <img src={icon_user_circle} className="empty_image" />
                    )}

                    {minorData.value !== 'detail' && !!isMenualUser && (
                      <>
                        <div className="partner_profile_image_edit_background">
                          <input
                            type="file"
                            accept=".gif,.png,.jpeg,.jpg"
                            id="partnerImage"
                            name="partnerImage"
                            hidden
                            ref={profileFileRef}
                            onChange={e => {
                              handleChangeProfileImage(e);
                            }}
                          />
                        </div>

                        <span className="partner_profile_image_edit_text">
                          <T>GLOBAL_UPLOAD</T>
                        </span>
                      </>
                    )}
                  </label>
                  {minorData.value !== 'detail' && !!isMenualUser && (
                    <IconButton
                      aria-label="delete_image"
                      className="partner_profile_image_delete"
                      onClick={handleDeleteProfileImage}
                    >
                      <img src={icon_trash} />
                    </IconButton>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  className={cx('partnerModalInput__form_box nickName', {
                    isDetail: !isModify.value,
                  })}
                >
                  <Grid item className="partner_info_row_accountType">
                    <CustomSpan fontSize={13} fontWeight={400} fontColor="#00A4E3">
                      {!!isMenualUser ? <T>GLOBAL_PERSONAL</T> : <T>GLOBAL_SYNC</T>}
                    </CustomSpan>
                  </Grid>
                  <Grid item className="partner_info_row_nick">
                    {minorData.value !== 'detail' && !!isMenualUser ? (
                      <Controller
                        name="nickName"
                        defaultValue={nickName.value}
                        control={partnerInfoControl}
                        rules={{ required: true }}
                        render={({ onChange, value, ref }) => (
                          <MuiWrapper>
                            <TextField
                              multiline
                              rows={1}
                              className="radius-md form__input_box"
                              variant="outlined"
                              fullWidth
                              placeholder={t('PLACEHOLDER_NICKNAME')}
                              error={!!partnerInfoErros.nickName}
                              value={value}
                              // onChange={onChange}
                              onChange={e => {
                                onChange(validInputString(e.target.value));
                              }}
                              inputRef={ref}
                            />
                          </MuiWrapper>
                        )}
                      />
                    ) : (
                      <CustomSpan fontSize={23} fontWeight={800} fontColor="#303030">
                        {nickName.value}
                      </CustomSpan>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item xs={12} className="partnerModalInput__form_wrapper ">
                <Grid
                  container
                  item
                  className={cx('partnerModalInput__form_box left', {
                    modify: !!isModify.value,
                  })}
                >
                  <Grid item className="partner_info_row partnerType">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_TYPE</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content partnerType', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' && !!isMenualUser ? (
                        <Controller
                          name="type"
                          defaultValue={type.value}
                          control={partnerInfoControl}
                          rules={{ required: true }}
                          // rules={{ required: false }}
                          render={({ onChange, value }) => (
                            <ButtonGroup className="partnerType_btn_group">
                              <MuiButton
                                disableElevation
                                className={cx('partnerType_btn md', {
                                  active: value === 1,
                                })}
                                data-type="radio"
                                onClick={() => onChange(1)}
                              >
                                <T>GLOBAL_CLINIC</T>
                              </MuiButton>
                              <MuiButton
                                disableElevation
                                className={cx('partnerType_btn md', {
                                  active: value === 2,
                                })}
                                data-type="radio"
                                onClick={() => onChange(2)}
                              >
                                <T>GLOBAL_LABORATORY</T>
                              </MuiButton>
                            </ButtonGroup>
                          )}
                        />
                      ) : (
                        <CustomSpan>
                          <T>{partnerTypeList.find(i => i.id === type.value)?.text}</T>
                        </CustomSpan>
                      )}
                    </div>
                  </Grid>
                  <Grid item className="partner_info_row email">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      {!!isMenualUser ? <T>GLOBAL_EMAIL</T> : <T>GLOBAL_SYNC_ID</T>}
                    </div>
                    <div
                      className={cx('partner_info_row_content email', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' && !!isMenualUser ? (
                        <Controller
                          name="email"
                          defaultValue={email.value}
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper>
                              <TextField
                                multiline
                                rows={1}
                                className="radius-md form__input_box"
                                variant="outlined"
                                fullWidth
                                // tpye="email"
                                placeholder={t('PLACEHOLDER_EMAIL')}
                                error={!!partnerInfoErros.email}
                                value={value}
                                // onChange={onChange}
                                onChange={e => {
                                  onChange(validInputString(e.target.value));
                                }}
                                inputRef={ref}
                              />
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan>{email.value}</CustomSpan>
                      )}
                    </div>
                  </Grid>

                  <Grid item className="partner_info_row phone">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_MOBILE</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content phone', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' && !!isMenualUser ? (
                        <Controller
                          name="phoneCode"
                          defaultValue=""
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper className="">
                              <FormControl variant="outlined" className="phoneCode_box">
                                <Select
                                  MenuProps={{
                                    anchorOrigin: {
                                      vertical: 'bottom',
                                      horizontal: 'left',
                                    },
                                    getContentAnchorEl: null,
                                  }}
                                  name="phoneCode"
                                  displayEmpty
                                  value={value}
                                  error={!!partnerInfoErros.phoneCode}
                                  renderValue={(selected: any) => {
                                    return countryList?.find((item: any) => item.id === selected)
                                      ?.phonecode;
                                  }}
                                  onChange={onChange}
                                >
                                  {countryList?.map((item: any, index: number) => {
                                    if (!item.phonecode) return null;
                                    return (
                                      <MenuItem value={item.id} key={index}>
                                        {item.phonecode && `${item.name} | ${item.phonecode}`}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan className="phoneCode">
                          {!!phoneCode.value &&
                            countryList?.find((item: any) => item.id === phoneCode.value)
                              ?.phonecode + ' )'}
                        </CustomSpan>
                      )}
                      {minorData.value !== 'detail' && !!isMenualUser ? (
                        <Controller
                          name="phone"
                          defaultValue=""
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper>
                              <TextField
                                multiline
                                rows={1}
                                className="radius-md form__input_box phone"
                                variant="outlined"
                                fullWidth
                                placeholder={t('PLACEHOLDER_ONLY_NUMBER')}
                                error={!!partnerInfoErros.phone}
                                value={value}
                                // onChange={onChange}
                                onChange={e => {
                                  if (!regPhoneNumber(e.target.value)) {
                                    return;
                                  } else {
                                    onChange(e);
                                  }
                                }}
                                inputRef={ref}
                              />
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan className="phone">{phone.value}</CustomSpan>
                      )}
                    </div>
                  </Grid>
                  <Grid item className="partner_info_row country">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_COUNTRY</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content country', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' && !!isMenualUser ? (
                        <Controller
                          name="country"
                          defaultValue=""
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper>
                              <FormControl fullWidth variant="outlined" className="countryId_box">
                                <Select
                                  MenuProps={{
                                    anchorOrigin: {
                                      vertical: 'bottom',
                                      horizontal: 'left',
                                    },
                                    getContentAnchorEl: null,
                                  }}
                                  name="country"
                                  displayEmpty
                                  value={value}
                                  error={!!partnerInfoErros.country}
                                  renderValue={(selected: any) => {
                                    return countryList?.find((item: any) => item.id === selected)
                                      ?.name;
                                  }}
                                  onChange={e => {
                                    onChange(e);
                                    country.setValue(e.target.value);
                                  }}
                                >
                                  {countryList?.map((item: any, index: number) => {
                                    if (!item.name) return null;
                                    return (
                                      <MenuItem value={item.id} key={index}>
                                        {item.phonecode && item.name}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan className="country">
                          {!!country.value &&
                            countryList?.find((item: any) => item.id === country.value)?.name}
                        </CustomSpan>
                      )}
                    </div>
                  </Grid>
                  <Grid item className="partner_info_row currency">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_CURRENCY</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content currency', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' ? (
                        <Controller
                          name="currency"
                          defaultValue=""
                          control={partnerInfoControl}
                          rules={{ required: true }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper className="">
                              <FormControl fullWidth variant="outlined">
                                <Select
                                  MenuProps={{
                                    anchorOrigin: {
                                      vertical: 'bottom',
                                      horizontal: 'left',
                                    },
                                    getContentAnchorEl: null,
                                  }}
                                  name="currency"
                                  displayEmpty
                                  value={value}
                                  error={!!partnerInfoErros.currency}
                                  onChange={onChange}
                                >
                                  <MenuItem value="" disabled>
                                    <T>GLOBAL_CHOOSE</T>
                                  </MenuItem>
                                  {currencyList?.map((item: any, index: number) => {
                                    return (
                                      <MenuItem value={item.code} key={index}>
                                        {item.feature} {item.name}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan>{currency.value}</CustomSpan>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  className={cx('partnerModalInput__form_box right', {
                    modify: !!isModify.value,
                  })}
                >
                  <Grid item className="partner_info_row fax">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_FAX</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content fax', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' ? (
                        <Controller
                          name="fax"
                          defaultValue=""
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper>
                              <TextField
                                multiline
                                rows={1}
                                name="fax"
                                className="radius-md form__input_box fax"
                                variant="outlined"
                                fullWidth
                                placeholder={t('PLACEHOLDER_ONLY_NUMBER')}
                                error={!!partnerInfoErros.fax}
                                value={value}
                                // onChange={onChange}
                                onChange={e => {
                                  if (!regPhoneNumber(e.target.value)) {
                                    return;
                                  } else {
                                    onChange(e);
                                  }
                                }}
                                inputRef={ref}
                              />
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan className="fax">{fax.value}</CustomSpan>
                      )}
                    </div>
                  </Grid>
                  <Grid item className="partner_info_row tel">
                    <div
                      className={cx('partner_info_row_label', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_TEL</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content tel', {
                        modify: !!isModify.value,
                      })}
                    >
                      <Controller
                        name="tel"
                        defaultValue=""
                        control={partnerInfoControl}
                        rules={{ required: false }}
                        render={({ onChange, value, ref }) =>
                          minorData.value !== 'detail' ? (
                            <MuiWrapper>
                              <TextField
                                multiline
                                rows={1}
                                className="radius-md form__input_box tel"
                                variant="outlined"
                                fullWidth
                                // type="number"
                                placeholder={t('PLACEHOLDER_ONLY_NUMBER')}
                                error={!!partnerInfoErros.tel}
                                value={value}
                                // onChange={onChange}
                                onChange={e => {
                                  if (!regPhoneNumber(e.target.value)) {
                                    return;
                                  } else {
                                    onChange(e);
                                  }
                                }}
                                inputRef={ref}
                              />
                            </MuiWrapper>
                          ) : (
                            <CustomSpan className="tel">{value}</CustomSpan>
                          )
                        }
                      />
                    </div>
                  </Grid>

                  <Grid item className="partner_info_row address">
                    <div
                      className={cx('partner_info_row_label address', {
                        modify: !!isModify.value,
                      })}
                    >
                      <T>GLOBAL_ADDRESS</T>
                    </div>
                    <div
                      className={cx('partner_info_row_content address', {
                        modify: !!isModify.value,
                      })}
                    >
                      {minorData.value !== 'detail' ? (
                        <Controller
                          name="address"
                          defaultValue={address.value}
                          control={partnerInfoControl}
                          rules={{ required: false }}
                          render={({ onChange, value, ref }) => (
                            <MuiWrapper className="address__input_wrapper">
                              <TextField
                                rows={4}
                                multiline
                                className="radius-md form__input_box address"
                                variant="outlined"
                                fullWidth
                                placeholder={t('PLACEHOLDER_PARTNER_ADDRESS')}
                                error={!!partnerInfoErros.address}
                                value={value}
                                onChange={e => {
                                  // 140byte
                                  if (getStringByte(e.target.value) > 140) {
                                    return;
                                  } else {
                                    // onChange(e);
                                    onChange(validInputString(e.target.value));
                                  }
                                }}
                                inputRef={ref}
                              />
                            </MuiWrapper>
                          )}
                        />
                      ) : (
                        <CustomSpan className="address">{address.value}</CustomSpan>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <div
              className={cx('partner_delete', {
                delete: minorData.value === 'detail',
              })}
            >
              <span onClick={onDeletePartner}>
                <T>GLOBAL_DELETE</T>
              </span>
            </div>
            {minorData.value === 'detail' && (
              <StyledPlainButtonOuter
                className="partnerModalInput__form_button_box detail"
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  config={{
                    width: '320px',
                  }}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round partner_ok_button "
                  onClick={() => {
                    isModify.setValue(true);
                    majorData.setValue('input');
                    minorData.setValue('edit');
                  }}
                >
                  <T>GLOBAL_MODIFY</T>
                </MuiButton>
              </StyledPlainButtonOuter>
            )}
            {minorData.value === 'edit' && (
              // 파트너 Modify
              <StyledPlainButtonOuter
                className="partnerModalInput__form_button_box edit"
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  type="submit"
                  config={{
                    width: '320px',
                  }}
                  disabled={!isImageChange && !isDirty}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round partner_ok_button "
                  onClick={(e: any) => {
                    // onSubmitPartnerData(e);
                  }}
                >
                  <T>GLOBAL_SAVE</T>
                </MuiButton>
              </StyledPlainButtonOuter>
            )}
            {minorData.value === 'new' && (
              // 파트너 수기 등록
              <StyledPlainButtonOuter
                className="partnerModalInput__form_button_box new"
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  type="submit"
                  config={{
                    width: '320px',
                  }}
                  disabled={!isDirty}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round partner_ok_button "
                >
                  {'+ '}
                  <T>GLOBAL_REGISTER</T>
                </MuiButton>
              </StyledPlainButtonOuter>
            )}
          </form>
        </div>
      </div>
    </StyledPartnerModalInput>
  );
}

const StyledPartnerModalInput = styled.section<{}>`
  .partnerModalInput__container {
    position: relative;
    width: 100%;
    margin-bottom: 30px;
    background-color: #ffffff;
    border-radius: 15px;
    height: 530px;
    padding-top: 10px;

    .partnerModalInput__wrapper {
      .partnerModalInput__wrapper_icon_box {
        position: absolute;
        top: -30px;
        right: 70px;
        transform: translate(0, -50%);
      }
      .partnerModalInput__form_container {
        flex-direction: column;
        .partnerModalInput__form_wrapper {
          &.top {
            min-height: 90px;
            margin-bottom: 10px;
          }
          margin-bottom: 20px;
        }

        .partner_info_row_nick {
          width: 300px;
        }
      }
      .partnerModalInput__form_box {
        width: 100%;

        &.modify {
          row-gap: 30px;
          width: 50%;
          &.left {
            padding-right: 40px;
            border-top: none;
            border-right: 1px dashed #b5b7c1;
          }
          &.right {
            padding-left: 40px;
          }
        }

        .partner_info_row {
          display: flex;
          width: 100%;

          .partner_info_row_label {
            width: 35%;
            max-width: 105px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            font-size: 15px;
            font-weight: 500;
            color: #17288a;
            &.modify {
              color: #303030;
              height: 40px;
            }
          }
          .partner_info_row_content {
            width: calc(100% - 105px);
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            &.address {
              max-width: 100%;
              padding-top: 5px;
              height: 73px;
              display: block;
              line-height: 25px;
              &.modify {
                height: 100px;
                margin-bottom: 80px;
                .form__input_box {
                  height: 100px;
                  margin-bottom: 80px;
                }
              }
            }

            &.modify {
              height: 40px;
            }
            .partnerType_btn_group {
              width: 100%;
              .partnerType_btn {
                width: 50%;
                height: 32px;
              }
            }

            &.phone {
              column-gap: 10px;
            }
            .phoneCode_box {
              width: 100px;
            }
          }
        }

        &.profileImg {
          width: 125px;
          height: 80px;
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;
          &.modify {
            width: 105px;
          }
          .profileImg__box {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            overflow: hidden;
            position: relative;
            box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);
            &.cursorPointer {
              &:hover {
                cursor: pointer;
              }
            }
            .empty_image {
              width: 80px;
              height: 80px;
            }
            .partner_profile_image_edit_background {
              position: absolute;
              bottom: 0;
              left: 0;
              width: 80px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.4;
              background-color: #000000;
              border-radius: 0 0 40px 40px / 0 0 30px 30px;
            }
            .partner_profile_image_edit_text {
              font-size: 12px;
              font-weight: 400;
              position: absolute;
              color: #ffffff;
              background-color: transparent;
              margin-bottom: 9px;
              left: 50%;
              bottom: 0;
              transform: translate(-50%, -0);
            }
          }
          .partner_profile_image_delete {
            width: 14px;
            height: 16px;
            margin-left: -5px;
            img {
              width: 14px;
              height: 16px;
            }
            &:hover {
              cursor: pointer;
            }
          }
        }
        &.nickName {
          row-gap: 10px;
          width: calc(100% - 125px);
          flex-direction: column;
          justify-content: flex-end;
          align-items: flex-start;
          &.isDetail {
            .partner_info_row_nick {
              width: 100%;
            }
          }
        }
      }

      .partner_delete {
        display: none;
        margin-top: 5px;
        &.delete {
          display: block;
        }
        text-align: center;
        span {
          color: #b5b7c1;
          text-decoration: underline;
          &:hover {
            cursor: pointer;
            color: #303030;
          }
        }
      }

      .partnerModalInput__form_button_box {
        .partner_ok_button {
          border: 1px solid #ffffff;
          position: relative;
          .partner_cancel_button {
            position: absolute;
            top: 50%;
            right: 10px;
            border: 1px solid #ffffff;
            border-radius: 18px;
            width: 36px;
            height: 36px;
            transform: translate(0, -50%);
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }

        .projectInformation__btn_box {
          position: relative;
          display: flex;
          .projectInformation__reject_btn {
            position: relative;
            z-index: 2;
          }
          .projectInformation__accept_btn {
            margin-left: -10px;
            background: rgb(0, 166, 226);
            background: linear-gradient(90deg, rgba(0, 166, 226, 1) 0%, rgba(8, 123, 238, 1) 100%);
            border: none;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            overflow: hidden;
            &:hover {
              background: ${`linear-gradient(
              90deg,
              ${Color('rgba(0, 166, 226, 1)').darken(0.12)} 0%,
              ${Color('rgba(8, 123, 238, 1)').darken(0.12)} 100%
            )`};
            }
            .btn-shadow-inset {
              position: absolute;
              top: -5px;
              left: -46px;
              width: 66px;
              height: 66px;
              border-radius: 33px;
              background-color: ${color.navy_blue};
              box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
            }
          }
        }
      }
    }
  }
`;

export default React.memo(PartnerModalInput);
